import { CommonModule } from '@angular/common';
import { Component, effect, OnInit, signal, ViewChildren } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageModule } from 'primeng/message';

import {
  VideoClipFormControl,
  VideoSceneFormControl,
} from '../video-project.form';
import { VideoShotAssetComponent } from '../video-shot-asset/video-shot-asset.component';
import { VideoPreviewClipComponent } from './video-preview-clip/video-preview-clip.component';

export interface VideoPreviewData {
  scenes: FormArray<FormGroup<VideoSceneFormControl>>;
}

@Component({
  selector: 'app-video-preview',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MessageModule,
    VideoShotAssetComponent,
    VideoPreviewClipComponent,
  ],
  templateUrl: './video-preview.component.html',
  styleUrl: './video-preview.component.scss',
})
export class VideoPreviewComponent implements OnInit {
  @ViewChildren('asset') assets: VideoShotAssetComponent[];

  readonly active = signal<FormGroup<VideoClipFormControl> | undefined>(
    undefined,
  );

  get scenes() {
    return this.config.data?.scenes?.controls ?? [];
  }

  get clips() {
    return this.scenes
      .flatMap(scene => {
        return scene.controls.shots.controls.flatMap(shot => {
          return shot.controls.clips.at(shot.value.selectedClipIndex);
        });
      })
      .filter(Boolean);
  }

  get resources() {
    return this.assets
      .map(asset => {
        return asset.resource;
      })
      .filter(Boolean);
  }

  get hasAnyResources() {
    return this.clips.some(clip => Boolean(clip.value.asset.resource));
  }

  constructor(private readonly config: DynamicDialogConfig<VideoPreviewData>) {
    effect(
      () => {
        const active = this.active();
        if (active) {
          this.onAfterActiveClipChange(active);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.first();
  }

  private onAfterActiveClipChange(clip: FormGroup<VideoClipFormControl>) {
    if (!clip.value.asset.resource) {
      this.next(clip);
      return;
    }
    const asset = this.resources.find(ref => {
      return ref.id === clip.value.id;
    });
    if (asset) {
      asset.play();
      asset.media.nativeElement.addEventListener(
        'ended',
        () => {
          this.next(clip);
        },
        { once: true },
      );
    }
  }

  onClipStart(clip: FormGroup<VideoClipFormControl>) {
    this.active.set(clip);
  }

  onRestart() {
    this.first();
  }

  private first() {
    const first = this.clips.find(clip => {
      return Boolean(clip.value.asset.resource);
    });
    this.active.set(first);
  }

  private next(clip: FormGroup<VideoClipFormControl>) {
    const next = this.clips.slice(this.clips.indexOf(clip) + 1).find(clip => {
      return Boolean(clip.value.asset.resource);
    });
    this.active.set(next);
  }

  isActiveClip(clip: FormGroup<VideoClipFormControl>) {
    return this.active()?.value?.id === clip.value.id;
  }
}
